import { defineComponent as _defineComponent } from 'vue';
import { toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, withCtx as _withCtx } from "vue";
const _hoisted_1 = { key: 0 };
import { computed, onBeforeUnmount, onMounted, ref, watchEffect } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import LoginLayout from './components/login-layout.vue';
import PhoneNumber from './components/phone-number.vue';
import PhoneCode from './components/phone-code.vue';
import { getLoginTheme } from './login-theme';
import { LoginAPI } from '@/api/login';
import { getCookie, setCookie } from '@/utils/common';
import { getLocalStorageItem, removeLocalStorageItem, setLocalStorageItem, setToken } from '@/utils/local-storage';
import { fetchUser } from '@/utils/user';
import { sendMetrics } from '@/utils/metrics';
import { trlMessage } from '@/services/i18n';
const BAD_PATTERN = 'value_error';
const MANY_REQUESTS = 'too_many_requests_error';
export default /*@__PURE__*/ _defineComponent({
    __name: 'login',
    setup(__props) {
        const route = useRoute();
        const router = useRouter();
        const step = ref('phone');
        const phoneLoading = ref(false);
        const phoneError = ref('');
        const retryCount = ref(0);
        const phonePayload = ref(null);
        const otpUuid = ref('');
        let timerId = null;
        const theme = computed(() => getLoginTheme(route.query.redirect));
        const formattedPhone = computed(() => formatPhone(phonePayload.value));
        const pageTitle = computed(() => normalizeTitle(theme.value.variant === 'event' ? theme.value.phoneTitle : trlMessage('header')));
        watchEffect(() => {
            document.title = pageTitle.value;
        });
        onMounted(() => {
            const number = getLocalStorageItem('phone');
            const countryCode = getLocalStorageItem('phonecode') || '+7';
            const nextRetry = getCookie('nextRetry');
            const uuid = getLocalStorageItem('otpUuid') || getCookie('uuid') || '';
            otpUuid.value = uuid;
            if (uuid) {
                setLocalStorageItem('otpUuid', uuid);
            }
            if (number) {
                phonePayload.value = { number, countryCode };
            }
            if (number && nextRetry && uuid && startTimer(decodeURIComponent(nextRetry))) {
                step.value = 'code';
            }
            else if (nextRetry || uuid) {
                clearOtpCookies();
            }
        });
        onBeforeUnmount(stopTimer);
        function requestCode(payload) {
            if (phoneLoading.value || retryCount.value > 0) {
                return;
            }
            const currentPayload = payload || phonePayload.value;
            if (!currentPayload) {
                step.value = 'phone';
                return;
            }
            phonePayload.value = currentPayload;
            phoneLoading.value = true;
            phoneError.value = '';
            setLocalStorageItem('phonecode', currentPayload.countryCode);
            setLocalStorageItem('phone', currentPayload.number);
            sendMetrics('code');
            LoginAPI.getCode({
                phone: `${currentPayload.countryCode}${currentPayload.number.replace(/\D/g, '')}`
            })
                .then((resp) => {
                otpUuid.value = resp.uuid;
                setLocalStorageItem('otpUuid', resp.uuid);
                setCookie('uuid', resp.uuid);
                setCookie('nextRetry', resp.next_retry_date);
                startTimer(resp.next_retry_date, resp.current_date);
                step.value = 'code';
            })
                .catch((error) => handlePhoneError(error))
                .finally(() => {
                phoneLoading.value = false;
            });
        }
        function handlePhoneError(error) {
            const body = error.response?.data?.errors?.[0];
            if (body?.type === BAD_PATTERN) {
                phoneError.value = trlMessage('error.BAD_PATTERN');
                step.value = 'phone';
                return;
            }
            if (body?.type === MANY_REQUESTS) {
                phoneError.value = trlMessage('error.ALREADY_SEND');
                setCookie('nextRetry', body.detail.next_retry_date);
                startTimer(body.detail.next_retry_date, body.detail.current_date);
                step.value = otpUuid.value ? 'code' : 'phone';
                return;
            }
            phoneError.value = body?.title || trlMessage('error.send.code');
            step.value = 'phone';
        }
        function startTimer(nextRetry, currentDate) {
            stopTimer();
            const currentTimestamp = currentDate ? new Date(currentDate).getTime() : Date.now();
            const retryTimestamp = new Date(nextRetry).getTime();
            if (!Number.isFinite(currentTimestamp) || !Number.isFinite(retryTimestamp)) {
                retryCount.value = 0;
                return false;
            }
            retryCount.value = Math.max(0, Math.floor((retryTimestamp - currentTimestamp) / 1000));
            if (retryCount.value === 0) {
                return false;
            }
            timerId = setInterval(() => {
                retryCount.value = Math.max(0, retryCount.value - 1);
                if (retryCount.value === 0) {
                    stopTimer();
                }
            }, 1000);
            return true;
        }
        function clearOtpCookies() {
            const expired = new Date(0);
            setCookie('nextRetry', '', { expires: expired });
            setCookie('uuid', '', { expires: expired });
            removeLocalStorageItem('otpUuid');
            otpUuid.value = '';
        }
        function stopTimer() {
            if (timerId) {
                clearInterval(timerId);
                timerId = null;
            }
        }
        function formatPhone(payload) {
            if (!payload) {
                return '';
            }
            const digits = payload.number.replace(/\D/g, '');
            if (payload.countryCode === '+7' && digits.length === 10) {
                return `+7 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 8)}-${digits.slice(8)}`;
            }
            return `${payload.countryCode} ${digits}`;
        }
        function normalizeTitle(title) {
            return title.replace(/\s+/g, ' ').trim();
        }
        function handleCodeComplete(token) {
            setCookie('nextRetry', '', { expires: -1 });
            setCookie('uuid', '', { expires: -1 });
            setToken(token.access_token, token.expiration_date);
            removeLocalStorageItem('phone');
            removeLocalStorageItem('phonecode');
            removeLocalStorageItem('otpUuid');
            otpUuid.value = '';
            stopTimer();
            fetchUser().then(() => {
                router.push({ name: 'orders' });
            });
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createBlock(LoginLayout, { theme: theme.value }, {
                default: _withCtx(() => [
                    (step.value === 'phone')
                        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                            _createElementVNode("h1", {
                                class: _normalizeClass(_ctx.$style.title)
                            }, _toDisplayString(theme.value.phoneTitle), 3),
                            _createVNode(PhoneNumber, {
                                loading: phoneLoading.value,
                                "retry-count": retryCount.value,
                                "error-message": phoneError.value,
                                onSubmit: requestCode,
                                onChange: _cache[0] || (_cache[0] = ($event) => (phoneError.value = ''))
                            }, null, 8, ["loading", "retry-count", "error-message"])
                        ]))
                        : (_openBlock(), _createBlock(PhoneCode, {
                            key: 1,
                            phone: formattedPhone.value,
                            uuid: otpUuid.value,
                            "retry-count": retryCount.value,
                            "retry-loading": phoneLoading.value,
                            onBack: _cache[1] || (_cache[1] = ($event) => (step.value = 'phone')),
                            onRetry: _cache[2] || (_cache[2] = ($event) => (requestCode())),
                            onComplete: handleCodeComplete
                        }, null, 8, ["phone", "uuid", "retry-count", "retry-loading"]))
                ]),
                _: 1
            }, 8, ["theme"]));
        };
    }
});
